import { cn } from "@/lib/utils";

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "fetchPriority"> & {
  /** Mark this as the LCP image: eager + fetchPriority="high". Use on ONE image per page. */
  priority?: boolean;
  /** Hint the browser to deprioritize this fetch — for far below-the-fold imagery. */
  lowPriority?: boolean;
  /** Manual override for fetchpriority. `priority`/`lowPriority` take precedence. */
  fetchPriority?: "high" | "low" | "auto";
};

/**
 * Responsive, lazy-by-default <img> with explicit width/height for zero CLS.
 *
 * Defaults to `loading="lazy"` + `decoding="async"` so below-the-fold images
 * never block initial render. Set `priority` for the LCP image; set
 * `lowPriority` for cards deep in the page so the browser can budget
 * bandwidth for what actually matters first.
 */
export function Img({
  priority = false,
  lowPriority = false,
  loading,
  decoding,
  fetchPriority,
  className,
  alt = "",
  width,
  height,
  ...rest
}: ImgProps) {
  const resolvedFetchPriority = priority
    ? "high"
    : lowPriority
      ? "low"
      : fetchPriority;
  return (
    <img
      {...rest}
      alt={alt}
      width={width}
      height={height}
      loading={loading ?? (priority ? "eager" : "lazy")}
      decoding={decoding ?? "async"}
      fetchPriority={resolvedFetchPriority}
      className={cn("block", className)}
    />
  );
}
